import { useState } from "react";
import { Typography } from "@material-tailwind/react";
import {
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import { SubscriptionPeriodRow, periodGrossUsdCents, periodNetUsdCents } from "api/subscriptionPeriods";
import { DateTime } from "luxon";

type TimelineEntry = SubscriptionPeriodRow;

type PlatformConfig = {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
};

const PLATFORM_CONFIG: Record<string, PlatformConfig> = {
  stripe: {
    label: "Stripe",
    color: "text-[#675DFF]",
    bgColor: "bg-[#675DFF]/10",
    borderColor: "border-[#675DFF]/40",
  },
  ios: {
    label: "Apple",
    color: "text-gray-700",
    bgColor: "bg-gray-100",
    borderColor: "border-gray-300",
  },
  square: {
    label: "Square",
    color: "text-teal-700",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-300",
  },
};

const DEFAULT_PLATFORM_CONFIG: PlatformConfig = {
  label: "Unknown",
  color: "text-blue-gray-500",
  bgColor: "bg-blue-gray-50",
  borderColor: "border-blue-gray-200",
};

type StatusBadge = {
  label: string;
  color: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const STATUS_BADGES: Record<string, StatusBadge> = {
  trial: {
    label: "Trial",
    color: "text-purple-600",
    Icon: ClockIcon,
  },
  refunded: {
    label: "Refunded",
    color: "text-red-600",
    Icon: XCircleIcon,
  },
  "billing-issues": {
    label: "Billing Issue",
    color: "text-amber-600",
    Icon: ExclamationTriangleIcon,
  },
  unsubscribed: {
    label: "Unsubscribed",
    color: "text-blue-gray-500",
    Icon: ArrowPathIcon,
  },
};

const formatTimestamp = (value?: Date | string | null): string => {
  if (!value) return "—";
  try {
    const dt = DateTime.fromISO(typeof value === "string" ? value : (value as Date).toISOString());
    return dt.isValid ? dt.toFormat("MM/dd/yyyy h:mm a") : "—";
  } catch {
    return "—";
  }
};

const formatCents = (cents?: number | null): string => {
  if (cents == null) return "—";
  const dollars = (cents / 100).toFixed(2);
  return `$${dollars}`;
};

type TimelineItemProps = {
  entry: TimelineEntry;
  isLast: boolean;
};

const TimelineItem = ({ entry, isLast }: TimelineItemProps) => {
  const [expanded, setExpanded] = useState(false);
  const platformConfig = PLATFORM_CONFIG[entry.platform ?? ""] ?? DEFAULT_PLATFORM_CONFIG;

  // Collect status badges
  const statusBadges: StatusBadge[] = [];
  if (entry.is_trial) statusBadges.push(STATUS_BADGES.trial);
  if (entry.refunded_at) statusBadges.push(STATUS_BADGES.refunded);
  if (entry.billing_issues_detected_at) statusBadges.push(STATUS_BADGES["billing-issues"]);
  if (entry.unsubscribe_detected_at && !entry.refunded_at) statusBadges.push(STATUS_BADGES.unsubscribed);

  // Extra fields for expandable detail panel
  const extraFields: [string, string][] = [];
  if (entry.plan) extraFields.push(["Plan", entry.plan]);
  if (entry.product_id) extraFields.push(["Product ID", entry.product_id]);
  if (entry.original_transaction_id) extraFields.push(["Subscription ID", entry.original_transaction_id]);
  if (entry.transaction_id) extraFields.push(["Transaction ID", entry.transaction_id]);
  if (entry.currency && entry.currency !== "USD") extraFields.push(["Currency", entry.currency]);
  if (entry.amount_cents != null) extraFields.push(["Gross Amount", formatCents(entry.amount_cents)]);
  if (entry.amount_usd_cents != null) extraFields.push(["Gross USD", formatCents(entry.amount_usd_cents)]);
  if (entry.effective_cents != null && entry.effective_cents !== entry.amount_cents) {
    extraFields.push(["Effective Amount", formatCents(entry.effective_cents)]);
  }
  if (entry.effective_usd_cents != null && entry.effective_usd_cents !== entry.amount_usd_cents) {
    extraFields.push(["Effective USD", formatCents(entry.effective_usd_cents)]);
  }
  if (!entry.is_trial && entry.commission_pct != null) extraFields.push(["Commission", `${(entry.commission_pct * 100).toFixed(1)}%`]);
  if (!entry.is_trial && entry.tax_pct != null) extraFields.push(["Tax", `${(entry.tax_pct * 100).toFixed(1)}%`]);
  if (entry.country) extraFields.push(["Country", entry.country]);
  if (entry.storefront) extraFields.push(["Storefront", entry.storefront]);
  if (entry.is_trial_conversion) extraFields.push(["Trial Conversion", "Yes"]);
  if (entry.transferred_from_account_id) {
    extraFields.push(["Transferred From", entry.transferred_from_account_id]);
  }

  const hasDetails = extraFields.length > 0 || statusBadges.length > 0;

  return (
    <div className="flex gap-3">
      {/* Left column: platform badge + vertical line */}
      <div className="flex flex-col items-center">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold ${platformConfig.bgColor} ${platformConfig.borderColor}`}
        >
          {entry.platform?.slice(0, 2).toUpperCase() ?? "??"}
        </div>
        {!isLast && <div className="mt-1 w-0.5 flex-1 bg-blue-gray-200" />}
      </div>

      {/* Right column: content */}
      <div className={`pb-6 ${isLast ? "" : ""}`}>
        <div
          className={`group relative cursor-pointer rounded-lg border px-4 py-3 shadow-sm transition-shadow hover:shadow-md ${platformConfig.bgColor} ${platformConfig.borderColor}`}
          onClick={() => hasDetails && setExpanded((v) => !v)}
        >
          {/* Top row: date + platform */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Typography variant="small" className="text-sm font-bold text-blue-gray-700">
              {formatTimestamp(entry.start_time)}
            </Typography>
            <span className={`text-sm font-normal ${platformConfig.color}`}>
              {platformConfig.label}
            </span>
          </div>

          {/* Status badges */}
          {statusBadges.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {statusBadges.map((badge) => {
                const Icon = badge.Icon;
                return (
                  <span key={badge.label} className={`flex items-center gap-1 text-xs font-medium ${badge.color}`}>
                    <Icon className="h-3.5 w-3.5" />
                    {badge.label}
                  </span>
                );
              })}
            </div>
          )}

          {/* Amount */}
          {(entry.effective_usd_cents != null || entry.amount_usd_cents != null) && (
            <Typography variant="small" className="mt-1 text-xs font-medium text-blue-gray-700">
              Net: {formatCents(periodNetUsdCents(entry))}
              {periodGrossUsdCents(entry) !== periodNetUsdCents(entry) && (
                <span className="ml-2 text-blue-gray-400">(Gross: {formatCents(periodGrossUsdCents(entry))})</span>
              )}
            </Typography>
          )}

          {/* Detail panel */}
          {hasDetails && expanded && (
            <div className="mt-3 border-t border-blue-gray-200 pt-3">
              <dl className="space-y-1">
                {extraFields.map(([label, value]) => (
                  <div key={label} className="flex gap-2 text-xs">
                    <dt className="min-w-[120px] font-medium text-blue-gray-600">{label}:</dt>
                    <dd className="text-blue-gray-800">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {hasDetails && (
            <Typography variant="small" className="mt-1 text-[10px] text-blue-gray-400">
              {expanded ? "▲ Hide details" : "▼ Show details"}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

type SubscriptionPeriodTimelineProps = {
  periods: TimelineEntry[];
  isLoading: boolean;
  error: string | null;
};

export const SubscriptionPeriodTimeline = ({
  periods,
  isLoading,
  error,
}: SubscriptionPeriodTimelineProps) => {
  if (isLoading) {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-blue-gray-400">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-gray-300 border-t-blue-500" />
        Loading subscription history…
      </div>
    );
  }

  if (error) {
    return (
      <Typography variant="small" color="red" className="mt-2">
        {error}
      </Typography>
    );
  }

  if (periods.length === 0) {
    return (
      <Typography variant="small" className="mt-2 italic text-blue-gray-400">
        No subscription history available.
      </Typography>
    );
  }

  return (
    <div className="mt-2">
      {periods.map((entry, index) => (
        <TimelineItem key={entry.id} entry={entry} isLast={index === periods.length - 1} />
      ))}
    </div>
  );
};

export default SubscriptionPeriodTimeline;
